import axios from 'axios';
import lodash from 'lodash';

// ACTION TYPES
const name = 'data';
export const actionTypes = {
  LOAD_SUBSCRIPTIONS: `${name}:load_subscriptions`,
  UPDATE_SELECTED_CAT1: `${name}:update_selected_cat1`,
  UPDATE_SUBSCRIPTIONS: `${name}:update_subscriptions`,
  LOAD_FUND_NAMES: `${name}:load_fund_names`,
  SAVE_IN_PROGRESS: `${name}:save_in_progress`,
  INVALIDATE_PARAMS: `${name}:invalidate_params`,
  SAVE_FAILED: `${name}:save_failed`,
  UNSUBSCRIBED_FROM_ALL: `${name}:unsubscribed_from_all`,
  SAVE_ALL: `${name}:save_all`,
};

const ALL_INVESTORS = 'All';
const EU_COUNTRIES = ['EU','CH','IT','DE','GB','JE','IE','FR','LU','DK','NL','NO','SE','FI','EU','AT','BE','ES','PL']

// Get the headers or params object for API validation
// Dataspan will accept a valid token/email combo or valid
// email / contactId (eloqua) combo
function getHeadersAndParams(params) {

  // If there is a token then use it
  const output = {
    headers: null,
    queryParams: {
      email: params.email,
      id: params.contactId,
      wtRegion: EU_COUNTRIES.includes(params.wtRegion)? 'EU': 'US',
    }
  };

  if (params.token) {
    output.headers = {
      Authorization: params.token &&
        `Token ${params.token}`,
    };
  }

  return output;

}

// Split out the text using '|' as a divider between languages and isoCode:: as
// the divider between the isocode and the text
function getTranslatedText(isoCode, text) {

  // Handle null text
  if (!text || text === ''){
    return text;
  }

  // Find the isoCode in the text. If not found then return original string
  const isoCodePosition = text.search(isoCode);

  // If isCode is US then return text, otherwise default to en-GB if not found (for EU countries)
  if (isoCodePosition === -1 && isoCode === 'en-US') {
    return text;
  } else if (isoCodePosition === -1){
    isoCode = 'en-GB';
  }

  // Split the text out for just the right isoCode provided
  try {
    const splitText = text.substring(isoCodePosition).split('|')[0].split('::')[1];
    return splitText;
  } 
  catch (error) {
    console.error(error);
    return text;
  }
}

// ACTIONS
export function loadSubscriptions() {
  return async (dispatch, getState) => {

    const params = getState().params;

    // Get headers/params
    const requestSetup = getHeadersAndParams(params);

    const request = {
      method: 'GET',
      url: `${params.dataspanApiUrl}/websitesubs/user_subscriptions/`,
      headers: requestSetup.headers,
      params: requestSetup.queryParams,
    };

    try {

      const response = await axios(request);

      // Check if the data array exists in the response
      if (response.data) {

        const contactData = response.data

        // Exclude any 'Hidden fields' based on the 'Hide' field on the Category Custom Object
        // Only include subscriptions for 'All' Investor types and the investor's category
        // Only include subscriptions that are valid for all countries, or the given user's country
        try{
          contactData.subscriptions = contactData.subscriptions.filter(sub => sub.hide !== true && 
          (sub.investorType === ALL_INVESTORS || sub.investorType === contactData.investorType) &&
          (( sub.countries && (sub.countries.search(params.wtRegion) > -1 || sub.countries === '')) || ( !sub.countries ))).sort((a, b) => {
            // Sort if the values exist for Order
            if(a.order && b.order){
              return parseInt(a.order) > parseInt(b.order) ? 1 : -1;
            }
            return 1
          });

        }
        catch (error) {
          console.error(error);
          contactData.subscriptions = [];
        }
                
        contactData.subscriptions.forEach(function (sub, index) {

          // Set index value
          sub.index = index;

          // Set up translatons
          sub.category2 = getTranslatedText(params.isoCode, sub.category2);
          sub.hover = getTranslatedText(params.isoCode, sub.hover);
          sub.translations = getTranslatedText(params.isoCode, sub.translations);
          sub.category2Subtitle = getTranslatedText(params.isoCode, sub.category2Subtitle);
          sub.footer = getTranslatedText(params.isoCode, sub.footer);
          sub.disclaimer = getTranslatedText(params.isoCode, sub.disclaimer);

          // Use a translated value if translations exists, otherwise use the standard subscription name
          sub.label = sub.translations ? sub.translations : sub.name;

        });

          // Update the subscriptions with fund names
          contactData.subscriptions = await loadFundNames(params, contactData.subscriptions);

          // Add group headings
          contactData.subscriptions = addGroups(params, contactData.subscriptions);

          dispatch({ type: actionTypes.LOAD_SUBSCRIPTIONS, value: contactData,  });

      }
    }
    catch(error){
      console.log(error);
      dispatch({ type: actionTypes.INVALIDATE_PARAMS });
    }

  };
}

function addGroups(params, subscriptions){

  const newSubscriptions = [];

  subscriptions.forEach( (sub, index) => {

    // Check if this sub has a grouping, and the previous sub doesn't have the same grouping
    if (sub.grouping && (index === 0 || (subscriptions[index-1] && subscriptions[index-1].grouping !== sub.grouping))){
      newSubscriptions.push({
        'name': getTranslatedText(params.isoCode, sub.grouping),
        'label': getTranslatedText(params.isoCode, sub.grouping),
        'apiField': null,
        'category1': sub.category1,
        'category2': sub.category2,
        'category3': sub.category3,
        'subscribed': subscriptions.filter( subToCheck => subToCheck.grouping === sub.grouping ).map ( subForGroup => subForGroup.subscribed).every(v => v === true),
      })
    }
    newSubscriptions.push(sub);

  })

  return newSubscriptions;

}

// Get the WT Fund names and tickers - used to transform some of the subscription names
function loadFundNames(params, subscriptions) {

  if (params.wtRegion !== 'undefined') {

    const request = {
      method: 'GET',
      url: `${params.dataspanApiUrl}/fundchar/wtfunds/`,
      params: {
        wtRegion: params.wtRegion
      }
    };

    return axios(request)
      .then(response => {

        // Check if the data array exists in the response
        if (response.data) {

          // Update labels for the subscriptions that start with 'F_' to indicate a fund name
          subscriptions.forEach(sub => {

            if (sub.name.slice(0, 2) === 'F_') {
              const fund = response.data.filter(i => i.ticker === sub.name.slice(2));

              if (fund.length > 0) {
                sub.label = `${fund[0].ticker} - ${fund[0].name.replace('WisdomTree ', '')}`;
              }

            }

          });

          return subscriptions;
        }
      })
      .catch(error => {
        console.log(error);
      });

  }
}

// Update the category 1 shown to the given one
export function updateSelectedCategory1(category1) {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.UPDATE_SELECTED_CAT1, value: category1 });
  };
}

// Update the subscription value
export function updateSubscriptionValue(subscription, newValue) {

  return (dispatch, getState) => {

    // GEt the data variables
    const contactData = getState().data.contactData;

    // Get the sub to update
    const subToUpdate = Object.assign({}, subscription);

    // Update the new subscription
    subToUpdate.subscribed = newValue;

    // Create an array of all subscriptions needing updating
    let subscriptionsToUpdate = [subToUpdate];

    // ////////////////////
    // // MANAGE GROUPS
    // ////////////////////

    // Handle changes to grouped subscriptions on the grouping row
    if (subToUpdate.grouping){

      // Find the grouping row for this subscription
      const groupedSubs = contactData.subscriptions.filter(sub => sub.name === subToUpdate.grouping);

      if (groupedSubs.length > 0){

        const groupedSub = Object.assign({}, groupedSubs[0]);
       
        // Check every other subscription is true for the grouping
        const allOthersChecked = contactData.subscriptions.filter( 
          subToCheck => subToCheck.grouping === subToUpdate.grouping && subToCheck.apiField !== subToUpdate.apiField ).map ( 
            subForGroup => subForGroup.subscribed).every(v => v === true);
        
        // If all the other subscriptions for grouping are true, then check if the new value is also true, otherwise set
        // the groupedSub to false
        groupedSub.subscribed = allOthersChecked && newValue ? true : false;
        subscriptionsToUpdate.push(groupedSub);
      }

    }

    // Handle changes to the grouping row
    if (!subToUpdate.apiField){

      const subsWithGrouping = contactData.subscriptions.filter( 
        subToCheck => subToCheck.grouping === subToUpdate.name).map( 
          subForGroup => {
            
            const updatedSubWithGrouping = Object.assign({}, subForGroup);
            updatedSubWithGrouping.subscribed = newValue
            return updatedSubWithGrouping
            
          });

      subscriptionsToUpdate = subscriptionsToUpdate.concat(subsWithGrouping)

    }

    // ////////////////////
    // // MANAGE HEADERS 
    // ////////////////////

    // // If this subscription is the master row to turn all the row's on or off for the same 'Header' value
    // // in a Table layout then get all the sub-subscriptions and check or uncheck their values too
    // if (subToUpdate.Check_All_For_Header && subToUpdate.Check_All_For_Header === '1') {

    //   // Find the relevant rows that share the same Header (and Categories)
    //   const subsWithSameHeader = data.subscriptions.filter(
    //     i =>
    //       i.header === subToUpdate.header &&
    //       i.category1 === subToUpdate.category1 &&
    //       i.category2 === subToUpdate.category2 &&
    //       i.category3 === subToUpdate.category3 &&
    //       i.apiField !== subToUpdate.apiField);

    //   // Update the value of all the subs
    //   subsWithSameHeader.forEach(sub => {

    //     const updatedSub = Object.assign({}, sub);
    //     // Set the sub to the new value - will be a checkbox type
    //     updatedSub.value = newValue === '1' ? updatedSub.checkedValue : updatedSub.uncheckedValue;
    //     subscriptionsToUpdate.push(updatedSub);
    //   });

    //   // Deal with the situation where the row being updated has a header. We need to ensure that the 'all header'
    //   // row (if it exists) is properly set by searching all the other rows for that header / category
    // } else if (subToUpdate.header) {

    //   // Find the relevant all header row
    //   const subWithAllHeader = data.subscriptions.filter(
    //     i =>
    //       i.header === subToUpdate.header &&
    //       i.category1 === subToUpdate.category1 &&
    //       i.category2 === subToUpdate.category2 &&
    //       i.category3 === subToUpdate.category3 &&
    //       i.apiField !== subToUpdate.apiField &&
    //       i.Check_All_For_Header === '1');

    //   // If there is an 'all header' row for this header 
    //   if (subWithAllHeader.length > 0) {

    //     // Find the relevant rows that share the same Header (and Categories)
    //     const subsWithSameHeader = data.subscriptions.filter(
    //       i =>
    //         i.header === subToUpdate.header &&
    //         i.category1 === subToUpdate.category1 &&
    //         i.category2 === subToUpdate.category2 &&
    //         i.category3 === subToUpdate.category3 &&
    //         i.apiField !== subToUpdate.apiField &&
    //         i.Check_All_For_Header !== '1');

    //     let allChecked = true;
    //     subsWithSameHeader.forEach(sub => {

    //       // Get the sub if it is being updated (through a group update)
    //       const updateInProgress = subscriptionsToUpdate.filter(subToUpdate => sub.apiField === subToUpdate.apiField);

    //       // Check if the sub (or update in progress sub) if unchecked. If any are unchecked then set the 
    //       // flag to false
    //       if ((sub.value === sub.uncheckedValue && updateInProgress.length === 0) ||
    //         (updateInProgress.length > 0 && updateInProgress[0].value === updateInProgress[0].uncheckedValue)) {
    //         allChecked = false;
    //       }
    //     })

    //     // Update the 'all headers' sub
    //     const updatedSub = Object.assign({}, subWithAllHeader[0]);
    //     updatedSub.value = allChecked ? updatedSub.checkedValue : updatedSub.uncheckedValue;
    //     subscriptionsToUpdate.push(updatedSub);

    //   }

    // }

    // Show the saving icon and update the UI
    dispatch({ type: actionTypes.UPDATE_SUBSCRIPTIONS, value: subscriptionsToUpdate });

    // Call to Dataspan to update all the subscriptions in the subscriptionsToUpdate array
    updateUserSubscriptions(getState().params, contactData, subscriptionsToUpdate, dispatch, false);

  };
}

// Unsubscribe from all subscriptions and set the Global unsub field to true
export function unsubscribeFromAll() {

  return (dispatch, getState) => {

    // GEt the data variables
    const contactData = getState().data.contactData;

    // Get all the subscriptions
    const updatedSubscriptions = lodash.cloneDeep(contactData.subscriptions);

    // Uncheck all the values
    updatedSubscriptions.forEach(sub => {
      sub.subscribed = false;
    });

    // Show the saving icon and update the UI
    dispatch({ type: actionTypes.UPDATE_SUBSCRIPTIONS, value: updatedSubscriptions });

    // Call to Dataspan to update all the subscriptions in the subscriptionsToUpdate array
    updateUserSubscriptions(getState().params, contactData, updatedSubscriptions, dispatch, true);

  };

}

// Save all the subscriptions (on the SAVE button)
export function saveAll() {

  return (dispatch, getState) => {

    // Get the data variables
    const contactData = getState().data.contactData;

    // Get all the subscriptions
    const updatedSubscriptions = lodash.cloneDeep(contactData.subscriptions);

    // Show the saving icon and update the UI
    dispatch({ type: actionTypes.UPDATE_SUBSCRIPTIONS, value: updatedSubscriptions });

    // Call to Dataspan to update all the subscriptions in the subscriptionsToUpdate array
    updateUserSubscriptions(getState().params, contactData, updatedSubscriptions, dispatch, false);

  };

}

// Update the user's subscription data
function updateUserSubscriptions(params, contactData, subscriptionsToUpdate, dispatch, globalOptOut = false) {

  // Show the saving icon
  dispatch({ type: actionTypes.SAVE_IN_PROGRESS, value: true });

  // Get headers/params
  const requestSetup = getHeadersAndParams(params);

  // Get the date of the opt out (today) if unsubscribing from all
  const optOutDate = globalOptOut === false ? null : new Date();

  const body = {
    id: contactData.id,
    email: contactData.email,
    type: contactData.type,
    globalOptOutDate: optOutDate,
    subscriptions: subscriptionsToUpdate.filter(sub => sub.apiField),
  };

  // Add the global Opt Out field ID
  if (globalOptOut) {
    // Set the unsubscribed date if its a global opt out
    dispatch({ type: actionTypes.UNSUBSCRIBED_FROM_ALL, value: optOutDate });
  }

  const request = {
    method: 'PUT',
    url: `${params.dataspanApiUrl}/websitesubs/update_user_subscriptions/`,
    headers: requestSetup.headers,
    params: requestSetup.queryParams,
    data: body,
  };

  return axios(request)
    .then(response => {

      if (response.status === 200) {

        dispatch({ type: actionTypes.SAVE_IN_PROGRESS, value: false });

      }

    })
    .catch(error => {

      // The save failed for some reason so show user an error
      dispatch({ type: actionTypes.SAVE_FAILED });

      // If there is an error then reload the app
      dispatch(loadSubscriptions());

      console.log(error);
      return false;
    });

}
